<template>
    <!-- Account -->
    <divider-generic
    icon="coin-drop"
    :applyBorder="false"
    :title="t('savings.mySavings.charge.step1.dividerTitleDeposit')"/>
    <text-block-generic
    :params="{
        label: t('savings.mySavings.charge.step1.productLabel'),
        content: selectedProduct.termAccountProduct.name
    }"/>
    <!-- Accordion -->
    <container-collapsible>
        <!-- description -->
        <text-block-generic
        :params="{
            content: selectedProduct.termAccountProduct.shortDescription
        }"/>
        <!-- information line -->
        <text-block-generic
        :params="{
            label: t('savings.mySavings.charge.step1.productDurationLabel'),
            content: t('savings.mySavings.charge.step1.productDurationContent', { numberOfDays: selectedProduct.numberOfDays })
        }"/>
        <text-block-generic
        :params="{
            label: t('savings.mySavings.charge.step1.productTanbLabel'),
            content: `${selectedProduct.interestRate}%`
        }"/>
        <text-block-generic
        :params="{
            label: t('savings.mySavings.charge.step1.productAmountAppliedLabel'),
            content: amountFormat(selectedProduct.balance, currency)
        }"/>
        <text-block-generic
        :params="{
            label: t('savings.mySavings.charge.step1.productMinAmountLabel'),
            content: amountFormat(selectedProduct.termAccountProduct.minimumAmount, currency)
        }"/>
        <text-block-generic
        :params="{
            label: t('savings.mySavings.charge.step1.productMaxAmountLabel'),
            content: amountFormat(selectedProduct.termAccountProduct.maximumAmount, currency)
        }"/>
        <text-block-generic
        :params="{
            label: t('savings.mySavings.charge.step1.productCreationDateLabel'),
            content: getDate(selectedProduct.creationDate)
        }"/>
        <text-block-generic
        :params="{
            label: t('savings.mySavings.charge.step1.productMaturityDateLabel'),
            content: getDate(selectedProduct.maturityDate),
            classes: 'capitalize'
        }"/>
    </container-collapsible>
    <!-- Source Account -->
    <divider-generic
    icon="account-balance"
    :title="t('savings.mySavings.charge.step1.dividerTitleAccount')"/>
    <!-- Dropdown -->
    <dropdown-account
    id="originAccountId"
    :params="accountDropdownParams"
    :options="accountsBalances"
    :selectedOption="formData.originAccountId"
    @clicked="[emit('formDataChange', { field: 'originAccountId', inputValue: $event }), clearFormErrors('originAccountId')]"/>
    <!-- Amount -->
    <divider-generic
    icon="bills"
    :title="t('savings.mySavings.charge.step1.dividerTitleAmount')"/>
    <!-- Input -->
    <input-amount
    id="amount"
    :params="amountParams"
    :inputValue="formData.amount"
    @input="[emit('formDataChange', { field: 'amount', inputValue: $event }), clearFormErrors('amount')]"
    @onBlur="amountValidation"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import ContainerCollapsible from '@/components/generic/containers/Collapsible'
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import InputAmount from '@/components/generic/inputs/Amount'

/* Vue */
import { ref, watch, onMounted } from 'vue'

/* Assets and Helpers */
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDate } from '@/assets/scripts/dateBuilders'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['scrollTo', 'formDataChange', 'resetShowMandatoryErrorsProp', 'formErrorsCleared'])

/* props received
@formData { Object } - object with info for our current form
@currency { String }
@selectedProduct { Array } - data that comes from the previous feature
@accountsBalances { Array } - list of accounts balances
@clearFormErrors { Boolean } - Prop to remove of form errors
@mandatoryList { Array } - Array of mandatory keys corresponding with our form
@showMandatoryErrors - { Boolean } - Prop to trigger display of mandatory errors
@isMobile: Boolean { Boolean }
*/
const props = defineProps({
    formData: Object,
    currency: String,
    selectedProduct: Array,
    accountsBalances: Array,
	clearFormErrors: Boolean,
    mandatoryList: Array,
    showMandatoryErrors: Boolean,
    isMobile: Boolean
})

/* Data */
const accountParams = ref({
    label: t('savings.mySavings.charge.step1.accountLabel'),
    content: props.selectedProduct.accountNumber,
    mask: ACCOUNT_NUMBER
})

const accountDropdownParams = ref({
	labelText: t('savings.mySavings.charge.step1.sourceAccountDropdownLabel'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

const amountParams = ref({
	labelText: t('savings.mySavings.charge.step1.amountLabel'),
	currency: props.currency,
    mandatory: true,
	errorState: false,
    errorMessage: null
})

const amountValidation = () => {
    if (props.formData.amount && props.formData.amount.length && parseInt(props.formData.amount) === 0) {
        amountParams.value.errorState = true
        amountParams.value.errorMessage = t('invalidAmount')
    }
}

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	originAccountIdParams: accountDropdownParams.value,
    amountParams: amountParams.value
})

watch(() => props.showMandatoryErrors, (newValue, oldValue) => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

    if (props.formData.amount && props.formData.amount.length && parseInt(props.formData.amount) === 0) {
        emit('scrollTo', getElementOffsetTopPosition('amount'))
        amountValidation()
    }

	if (mandatoryFieldsEmptyList.length > 0) emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

    mandatoryFieldsEmptyList.forEach(fieldName => {
        allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        allFormFields.value[`${fieldName}Params`].errorState = true
    })
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, (newValue, oldValue) => {
    clearFormErrors()
    emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}

onMounted(() => {
    emit('scrollTo', 0)
})
</script>

<style lang="scss" scoped>
</style>
